// Шестерни: прямые, косые и ёлочка; одна или пара. Отверстие под вал (круглое, с лыской, шестигранник),
// ступица со стопорным винтом M3 и гайкой, зазор в паре, фаска по краю зубьев. Пара в просмотре стоит
// в зацеплении и крутится; в файле шестерни лежат рядом.
import { bedPicker, bedWarn, createStage, fitsBed, scope, solid, toGeometry, type Ctx, type Model } from '@gen3d/core';
import type { CrossSection, Manifold, ManifoldToplevel } from 'manifold-3d/manifold';
import { centerDistance, circles, outline, undercut, type Spec } from './gear';

type NumKey = 'M' | 'Z1' | 'Z2' | 'B' | 'BETA' | 'D1' | 'DF1' | 'HX1' | 'D2' | 'DF2' | 'HX2' | 'TOL' | 'HD' | 'HH' | 'BL' | 'CH' | 'X1' | 'X2' | 'DA';
type Hole = 'none' | 'round' | 'd' | 'hex';
type S = Record<NumKey, number> & {
  kind: 'spur' | 'helix' | 'herring'; pair: boolean; alpha: '14.5' | '20' | '25';
  hole1: Hole; hole2: Hole; same: boolean; hub: boolean; screw: boolean; view: 'mesh' | 'bed'; spin: boolean;
};

const T = {
  uk: {
    gear: 'Шестерня', kind: 'Зуби', spur: 'Прямі', helix: 'Косі', herring: 'Ялинка',
    one: 'Одна', pair: 'Пара', M: 'Модуль', Z1: 'Зубів', Z1p: 'Зубів у першої', Z2: 'Зубів у другої', B: 'Товщина', BETA: 'Нахил зубів',
    hintM: 'Модуль — розмір зуба. Шестерні зачіпляються, лише якщо модуль однаковий.',
    hintKind: 'Косі зуби крутяться тихіше, але штовхають шестерню вздовж осі. Ялинка — без цього поштовху і друкується без підтримок.',
    info: '{n}: ділильний Ø {d} · зовнішній Ø {da} · западин Ø {df} мм',
    first: 'Перша', second: 'Друга', dist: 'Міжосьова відстань {a} мм · передаточне {r}',
    under: 'Менше {z} зубів — зуб підрізається біля основи і може заїдати. Поставте зміщення від {x} у «Додатково».',
    old: 'Підібрати під стару шестерню', DA: 'Зовнішній діаметр старої',
    oldHint: 'Поміряйте штангенциркулем по вершинах зубів і порахуйте зуби — у полі «Зубів» вище.',
    oldCalc: 'Модуль ≈ {m} мм → стандартний {s} мм', oldFar: 'Помітно не збігається зі стандартним — можливо, шестерня зі зміщенням або дюймова.',
    oldApply: 'Поставити {s} мм',
    holeG: 'Отвір', hole: { none: 'Немає', round: 'Круглий', d: 'Лиска', hex: 'Шестигранник' } as Record<Hole, string>,
    D: 'Діаметр вала', DF: 'Розмір по лисці', HX: 'Розмір під ключ', TOL: 'Запас на посадку',
    same2: 'У другої такий самий отвір', secondHole: 'Отвір другої шестерні',
    tolHint: 'Отвори друкуються трохи меншими — запас додається до розміру.',
    holeBig: 'Отвір більший за шестерню — зменште його.',
    hubG: 'Маточина', hub: 'Маточина', HD: 'Діаметр маточини', HH: 'Висота маточини', screw: 'Стопорний гвинт M3 з гайкою',
    hubBoth: 'Маточина — в обох шестерень, з одного боку.',
    screwHint: 'Гвинт M3 упирається у вал{flat}, гайка вставляється в паз згори.', flat: ' (у лиску)',
    nutNo: 'Гайка M3 не вміщується: маточина має бути не менше Ø {d} мм.',
    hubHit: 'Маточини двох шестерень зачіпають одна одну — зменште діаметр.',
    printG: 'Друк', BL: 'Зазор між зубами', CH: 'Фаска по краю зубів',
    blHint: 'Без зазору надрукована пара заклинить. Туго крутиться — збільште.',
    chHint: 'Знімає «слонячу ногу» першого шару, шестерні легше входять у зачеплення.',
    view: 'Перегляд', mesh: 'У зачепленні', bed: 'Як на столі', spin: 'Крутити',
    more: 'Додатково', alpha: 'Кут зачеплення', X1: 'Зміщення профілю', X1p: 'Зміщення першої', X2: 'Зміщення другої',
    alphaHint: '20° — стандарт. 14,5° — старі механізми, 25° — міцніший зуб.',
    xHint: 'Зміщення в частках модуля: прибирає підрізання в малої шестерні або підганяє пару під потрібну відстань.',
    mm: 'мм', pcs: 'шт', deg: '°', min: 'мін.', max: 'макс.', ok: 'Вміщується на стіл', err: 'Не вдалося побудувати модель',
  },
  ru: {
    gear: 'Шестерня', kind: 'Зубья', spur: 'Прямые', helix: 'Косые', herring: 'Ёлочка',
    one: 'Одна', pair: 'Пара', M: 'Модуль', Z1: 'Зубьев', Z1p: 'Зубьев у первой', Z2: 'Зубьев у второй', B: 'Толщина', BETA: 'Наклон зубьев',
    hintM: 'Модуль — размер зуба. Шестерни зацепляются, только если модуль одинаковый.',
    hintKind: 'Косые зубья крутятся тише, но толкают шестерню вдоль оси. Ёлочка — без этого толчка и печатается без поддержек.',
    info: '{n}: делительный Ø {d} · наружный Ø {da} · впадин Ø {df} мм',
    first: 'Первая', second: 'Вторая', dist: 'Межосевое расстояние {a} мм · передаточное {r}',
    under: 'Меньше {z} зубьев — зуб подрезается у основания и может заедать. Поставьте смещение от {x} в «Дополнительно».',
    old: 'Подобрать под старую шестерню', DA: 'Наружный диаметр старой',
    oldHint: 'Измерьте штангенциркулем по вершинам зубьев и посчитайте зубья — в поле «Зубьев» выше.',
    oldCalc: 'Модуль ≈ {m} мм → стандартный {s} мм', oldFar: 'Заметно не совпадает со стандартным — возможно, шестерня со смещением или дюймовая.',
    oldApply: 'Поставить {s} мм',
    holeG: 'Отверстие', hole: { none: 'Нет', round: 'Круглое', d: 'Лыска', hex: 'Шестигранник' } as Record<Hole, string>,
    D: 'Диаметр вала', DF: 'Размер по лыске', HX: 'Размер под ключ', TOL: 'Запас на посадку',
    same2: 'У второй такое же отверстие', secondHole: 'Отверстие второй шестерни',
    tolHint: 'Отверстия печатаются чуть меньше — запас прибавляется к размеру.',
    holeBig: 'Отверстие больше шестерни — уменьшите его.',
    hubG: 'Ступица', hub: 'Ступица', HD: 'Диаметр ступицы', HH: 'Высота ступицы', screw: 'Стопорный винт M3 с гайкой',
    hubBoth: 'Ступица — у обеих шестерён, с одной стороны.',
    screwHint: 'Винт M3 упирается в вал{flat}, гайка вставляется в паз сверху.', flat: ' (в лыску)',
    nutNo: 'Гайка M3 не помещается: ступица должна быть не меньше Ø {d} мм.',
    hubHit: 'Ступицы двух шестерён задевают друг друга — уменьшите диаметр.',
    printG: 'Печать', BL: 'Зазор между зубьями', CH: 'Фаска по краю зубьев',
    blHint: 'Без зазора напечатанная пара заклинит. Туго крутится — увеличьте.',
    chHint: 'Убирает «слоновью ногу» первого слоя, шестерни легче входят в зацепление.',
    view: 'Просмотр', mesh: 'В зацеплении', bed: 'Как на столе', spin: 'Крутить',
    more: 'Дополнительно', alpha: 'Угол зацепления', X1: 'Смещение профиля', X1p: 'Смещение первой', X2: 'Смещение второй',
    alphaHint: '20° — стандарт. 14,5° — старые механизмы, 25° — зуб прочнее.',
    xHint: 'Смещение в долях модуля: убирает подрезание у маленькой шестерни или подгоняет пару под нужное расстояние.',
    mm: 'мм', pcs: 'шт', deg: '°', min: 'мин.', max: 'макс.', ok: 'Помещается на стол', err: 'Не удалось построить модель',
  },
  en: {
    gear: 'Gear', kind: 'Teeth', spur: 'Straight', helix: 'Helical', herring: 'Herringbone',
    one: 'Single', pair: 'Pair', M: 'Module', Z1: 'Teeth', Z1p: 'Teeth on the first', Z2: 'Teeth on the second', B: 'Thickness', BETA: 'Helix angle',
    hintM: 'The module is the tooth size. Gears mesh only if their module is the same.',
    hintKind: 'Helical teeth run quieter but push the gear along its axis. Herringbone has no such push and prints without supports.',
    info: '{n}: pitch Ø {d} · outer Ø {da} · root Ø {df} mm',
    first: 'First', second: 'Second', dist: 'Center distance {a} mm · ratio {r}',
    under: 'Below {z} teeth the tooth is undercut at the root and may bind. Set a profile shift of {x} or more under “More”.',
    old: 'Match an old gear', DA: 'Outer diameter of the old gear',
    oldHint: 'Measure across the tooth tips with calipers and count the teeth — in the “Teeth” field above.',
    oldCalc: 'Module ≈ {m} mm → standard {s} mm', oldFar: 'Noticeably off the standard — the gear may have a profile shift or be imperial.',
    oldApply: 'Use {s} mm',
    holeG: 'Bore', hole: { none: 'None', round: 'Round', d: 'D-shaft', hex: 'Hex' } as Record<Hole, string>,
    D: 'Shaft diameter', DF: 'Across the flat', HX: 'Across flats', TOL: 'Fit allowance',
    same2: 'Same bore on the second gear', secondHole: 'Bore of the second gear',
    tolHint: 'Holes print slightly undersize — the allowance is added to the size.',
    holeBig: 'The bore is bigger than the gear — make it smaller.',
    hubG: 'Hub', hub: 'Hub', HD: 'Hub diameter', HH: 'Hub height', screw: 'M3 set screw with a nut',
    hubBoth: 'Both gears get the hub, on the same side.',
    screwHint: 'The M3 screw presses on the shaft{flat}; the nut drops into a slot from the top.', flat: ' (on the flat)',
    nutNo: 'The M3 nut does not fit: the hub must be at least Ø {d} mm.',
    hubHit: 'The hubs of the two gears hit each other — reduce the diameter.',
    printG: 'Printing', BL: 'Backlash', CH: 'Tooth edge chamfer',
    blHint: 'Without backlash a printed pair will jam. If it turns stiffly, increase it.',
    chHint: 'Removes the first-layer “elephant foot” and helps the gears slide into mesh.',
    view: 'Preview', mesh: 'Meshed', bed: 'As on the bed', spin: 'Rotate',
    more: 'More', alpha: 'Pressure angle', X1: 'Profile shift', X1p: 'Shift of the first', X2: 'Shift of the second',
    alphaHint: '20° is the standard. 14.5° — older machines, 25° — a stronger tooth.',
    xHint: 'Shift in fractions of the module: removes undercut on a small gear or fits a pair to the center distance you need.',
    mm: 'mm', pcs: 'pcs', deg: '°', min: 'min', max: 'max', ok: 'Fits the bed', err: 'Could not build the model',
  },
};

// [мин, макс, шаг, по умолчанию]
const LIM: Record<NumKey, [number, number, number, number]> = {
  M: [0.3, 10, 0.05, 1.5], Z1: [6, 200, 1, 20], Z2: [6, 300, 1, 40], B: [1, 100, 0.1, 8], BETA: [5, 45, 0.5, 20],
  D1: [1, 100, 0.1, 5], DF1: [0.5, 100, 0.1, 4.5], HX1: [1, 100, 0.1, 5.5],
  D2: [1, 100, 0.1, 5], DF2: [0.5, 100, 0.1, 4.5], HX2: [1, 100, 0.1, 5.5], TOL: [0, 1, 0.01, 0.2],
  HD: [3, 200, 0.1, 16], HH: [1, 50, 0.1, 6], BL: [0, 1, 0.01, 0.15], CH: [0, 3, 0.05, 0.4],
  X1: [-0.6, 1.2, 0.01, 0], X2: [-0.6, 1.2, 0.01, 0], DA: [2, 600, 0.01, 33],
};
const UNIT: Partial<Record<NumKey, 'pcs' | 'deg' | ''>> = { Z1: 'pcs', Z2: 'pcs', BETA: 'deg', X1: '', X2: '' };
/** Ряд модулей (ISO 54, оба ряда, от 0,3 до 10). */
const MODULES = [0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.125, 1.25, 1.375, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.5, 4, 4.5, 5, 5.5, 6, 7, 8, 9, 10];
// гвинт і гайка M3 (ISO 4032): отвір під гвинт, розмір під ключ, товщина
const M3 = { hole: 3.2, s: 5.5, t: 2.4 };
const GAP = 6; // між шестернями на столі

const clamp = (v: number, [a, b]: readonly number[]) => Math.min(b, Math.max(a, v));
const r2 = (v: number) => Math.round(v * 100) / 100;

type Store = { load(): unknown; save(v: unknown): void };

export function mount(root: HTMLElement, { lang, store }: Ctx & { store?: Store }) {
  const t = T[lang] ?? T.uk;
  const num = (v: number) => (lang === 'en' ? String(r2(v)) : String(r2(v)).replace('.', ','));
  const s = { kind: 'spur', pair: false, alpha: '20', hole1: 'round', hole2: 'round', same: true, hub: false, screw: true, view: 'mesh',
    spin: !matchMedia('(prefers-reduced-motion: reduce)').matches } as S;
  for (const k of Object.keys(LIM) as NumKey[]) s[k] = LIM[k][3];
  const holes: Hole[] = ['none', 'round', 'd', 'hex'];
  try {
    const v = (store?.load() as { s?: Record<string, any> } | null)?.s;
    if (v && typeof v === 'object') {
      for (const k of Object.keys(LIM) as NumKey[]) if (typeof v[k] === 'number' && isFinite(v[k])) s[k] = clamp(v[k], LIM[k]);
      if (['spur', 'helix', 'herring'].includes(v.kind)) s.kind = v.kind;
      if (['14.5', '20', '25'].includes(v.alpha)) s.alpha = v.alpha;
      if (holes.includes(v.hole1)) s.hole1 = v.hole1;
      if (holes.includes(v.hole2)) s.hole2 = v.hole2;
      if (v.view === 'mesh' || v.view === 'bed') s.view = v.view;
      for (const b of ['pair', 'same', 'hub', 'screw', 'spin'] as const) if (typeof v[b] === 'boolean') s[b] = v[b];
    }
  } catch { /* типовые */ }

  const stage = root.querySelector<HTMLElement>('[data-stage]')!;
  const panel = root.querySelector<HTMLElement>('[data-params]')!;
  const viewer = createStage(stage, lang).viewer;

  // ---------- панель ----------
  const rng = (k: NumKey, label: string = (t as Record<string, any>)[k] ?? k) => {
    const [min, max, step] = LIM[k], u = UNIT[k] ?? 'mm';
    return `<div class="rng" data-k="${k}"><span class="lbl" data-l="${k}">${label}</span><span class="rng-row">` +
      `<input type="range" min="${min}" max="${max}" step="${step}" value="${s[k]}" aria-label="${label}">` +
      `<span class="num"><input type="number" min="${min}" max="${max}" step="${step}" value="${s[k]}" inputmode="decimal" aria-label="${label}"><i>${u ? t[u] : ''}</i></span></span>` +
      `<span class="minmax">${t.min} ${num(min)} · ${t.max} ${num(max)}</span></div>`;
  };
  const seg = (k: string, opts: [string, string][], cur: string) =>
    `<div class="seg wide" data-seg="${k}">${opts.map(([v, l]) => `<button type="button" data-v="${v}" class="${v === cur ? 'on' : ''}">${l}</button>`).join('')}</div>`;
  const sw = (k: string, label: string, on: boolean) => `<label class="switch"><input type="checkbox" data-sw="${k}"${on ? ' checked' : ''}><span>${label}</span></label>`;
  const holeBlock = (n: 1 | 2) =>
    seg(`hole${n}`, holes.map((h) => [h, t.hole[h]]), s[`hole${n}`]) +
    `<div data-hd${n}>${rng(`D${n}` as NumKey, t.D)}<div data-df${n}>${rng(`DF${n}` as NumKey, t.DF)}</div></div><div data-hx${n}>${rng(`HX${n}` as NumKey, t.HX)}</div>`;

  panel.innerHTML =
    `<details class="group" open><summary>${t.gear}</summary>` +
      seg('pair', [['one', t.one], ['pair', t.pair]], s.pair ? 'pair' : 'one') +
      `<span class="lbl gap">${t.kind}</span>` + seg('kind', [['spur', t.spur], ['helix', t.helix], ['herring', t.herring]], s.kind) +
      '<p class="hint" data-hint-kind></p>' +
      rng('M') + '<p class="hint">' + t.hintM + '</p>' + rng('Z1') + `<div data-z2>${rng('Z2')}</div>` + rng('B') + `<div data-beta>${rng('BETA')}</div>` +
      '<p class="hint calc" data-info></p><p class="hint calc" data-under></p></details>' +
    `<details class="group"><summary>${t.old}</summary>` + rng('DA') + `<p class="hint">${t.oldHint}</p>` +
      '<p class="hint calc" data-old></p><div class="chips"><button type="button" class="chip" data-apply-m></button></div></details>' +
    `<details class="group" open><summary>${t.holeG}</summary>` + holeBlock(1) + rng('TOL') + `<p class="hint">${t.tolHint}</p>` +
      `<div data-pair-hole>${sw('same', t.same2, s.same)}<div data-hole2><span class="lbl gap">${t.secondHole}</span>${holeBlock(2)}</div></div>` +
      '<p class="hint calc" data-hole-big></p></details>' +
    `<details class="group"><summary>${t.hubG}</summary>` + sw('hub', t.hub, s.hub) +
      `<div data-hub-opts>${rng('HD')}${rng('HH')}<div data-screw>${sw('screw', t.screw, s.screw)}</div><p class="hint" data-hub-both>${t.hubBoth}</p><p class="hint calc" data-hub-info></p></div></details>` +
    `<details class="group" open><summary>${t.printG}</summary>` + rng('BL') + `<p class="hint">${t.blHint}</p>` + rng('CH') + `<p class="hint">${t.chHint}</p>` +
      `<div data-view><span class="lbl gap">${t.view}</span>${seg('view', [['mesh', t.mesh], ['bed', t.bed]], s.view)}<div data-spin>${sw('spin', t.spin, s.spin)}</div></div></details>` +
    `<details class="group"><summary>${t.more}</summary><span class="lbl">${t.alpha}</span>` +
      seg('alpha', [['14.5', '14,5°'.replace(',', lang === 'en' ? '.' : ',')], ['20', '20°'], ['25', '25°']], s.alpha) + `<p class="hint">${t.alphaHint}</p>` +
      rng('X1', t.X1) + `<div data-x2>${rng('X2', t.X2)}</div><p class="hint">${t.xHint}</p></details>`;

  const $ = <E extends Element = HTMLElement>(q: string) => panel.querySelector<E>(q)!;
  const setNum = (k: NumKey, v: number) => {
    s[k] = clamp(v, LIM[k]);
    panel.querySelectorAll<HTMLInputElement>(`[data-k="${k}"] input`).forEach((i) => (i.value = String(s[k])));
  };
  panel.querySelectorAll<HTMLElement>('.rng').forEach((el) => {
    const k = el.dataset.k as NumKey;
    const [r, n] = el.querySelectorAll('input');
    const set = (raw: string) => {
      const x = raw.trim() === '' ? NaN : +raw.replace(',', '.');
      setNum(k, Number.isFinite(x) ? x : s[k]);
      sync(); if (k !== 'DA') rebuild();
    };
    r.oninput = () => set(r.value);
    n.onchange = () => set(n.value);
  });
  panel.querySelectorAll<HTMLElement>('[data-seg]').forEach((el) => {
    el.onclick = (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-v]');
      if (!b) return;
      el.querySelectorAll('button').forEach((x) => x.classList.toggle('on', x === b));
      const k = el.dataset.seg!, v = b.dataset.v!;
      if (k === 'pair') s.pair = v === 'pair'; else (s as any)[k] = v;
      sync(); rebuild();
    };
  });
  panel.querySelectorAll<HTMLInputElement>('[data-sw]').forEach((el) => {
    el.onchange = () => {
      (s as any)[el.dataset.sw!] = el.checked;
      if (el.dataset.sw === 'spin') { save(); return; }
      sync(); rebuild();
    };
  });
  $('[data-apply-m]').onclick = () => { const m = nearest(); setNum('M', m); sync(); rebuild(); };
  let bed = bedPicker(panel, lang, (b) => { bed = b; viewer.setBed(b.x, b.y); rebuild(); });
  viewer.setBed(bed.x, bed.y);

  // ---------- расчёт ----------
  const beta = (p: S) => (p.kind === 'spur' ? 0 : p.BETA);
  const spec = (p: S, n: 1 | 2): Spec => ({ m: p.M, z: n === 1 ? p.Z1 : p.Z2, alpha: +p.alpha, beta: beta(p), x: n === 1 ? p.X1 : p.X2, backlash: p.BL });
  /** Модуль по наружному диаметру старой шестерни (без смещения). */
  const fromOld = () => s.DA / (s.Z1 / Math.cos((beta(s) * Math.PI) / 180) + 2);
  const nearest = () => MODULES.reduce((a, b) => (Math.abs(b - fromOld()) < Math.abs(a - fromOld()) ? b : a));
  type HoleP = { kind: Hole; d: number; df: number; hx: number };
  const holeOf = (p: S, n: 1 | 2): HoleP => {
    const k = n === 2 && p.same ? 1 : n;
    const kind = k === 1 ? p.hole1 : p.hole2, d = (k === 1 ? p.D1 : p.D2) + p.TOL;
    return { kind, d, df: Math.min((k === 1 ? p.DF1 : p.DF2) + p.TOL, d - 0.1), hx: (k === 1 ? p.HX1 : p.HX2) + p.TOL };
  };
  /** Радиус описанной окружности отверстия. */
  const holeR = (h: HoleP) => (h.kind === 'none' ? 0 : h.kind === 'hex' ? h.hx / Math.sqrt(3) : h.d / 2);
  /** Гайка помещается в ступицу между отверстием и краем: минимальный диаметр ступицы. */
  const nutMinD = (h: HoleP, tol: number) => 2 * (holeR(h) + M3.t + tol + 1.6);

  function sync() {
    const g1 = spec(s, 1), g2 = spec(s, 2), c1 = circles(g1), c2 = circles(g2);
    $('[data-z2]').hidden = !s.pair; $('[data-x2]').hidden = !s.pair;
    $('[data-beta]').hidden = s.kind === 'spur';
    $('[data-l="Z1"]').textContent = s.pair ? t.Z1p : t.Z1;
    $('[data-l="X1"]').textContent = s.pair ? t.X1p : t.X1;
    $('[data-hint-kind]').textContent = t.hintKind; $('[data-hint-kind]').hidden = s.kind === 'spur';
    const line = (n: string, c: ReturnType<typeof circles>) => t.info.replace('{n}', n).replace('{d}', num(2 * c.r)).replace('{da}', num(2 * c.ra)).replace('{df}', num(2 * c.rf));
    $('[data-info]').innerHTML = s.pair
      ? `${line(t.first, c1)}<br>${line(t.second, c2)}<br>${t.dist.replace('{a}', num(centerDistance(g1, g2))).replace('{r}', `1 : ${num(s.Z2 / s.Z1)}`)}`
      : line(t.gear, c1).replace(`${t.gear}: `, '');
    const u = [g1, ...(s.pair ? [g2] : [])].map(undercut), bad = [g1, ...(s.pair ? [g2] : [])].findIndex((g, i) => g.x < u[i].xmin - 0.005);
    $('[data-under]').hidden = bad < 0;
    if (bad >= 0) $('[data-under]').textContent = t.under.replace('{z}', String(Math.ceil(u[bad].zmin))).replace('{x}', num(Math.ceil(u[bad].xmin * 100) / 100));
    // подбор по старой
    const m = fromOld(), st = nearest();
    $('[data-old]').innerHTML = t.oldCalc.replace('{m}', num(Math.round(m * 1000) / 1000)).replace('{s}', num(st)) + (Math.abs(m - st) / st > 0.03 ? `<br>${t.oldFar}` : '');
    $('[data-apply-m]').textContent = t.oldApply.replace('{s}', num(st));
    $('[data-apply-m]').hidden = st === s.M;
    // отверстие
    for (const n of [1, 2] as const) {
      const k = s[`hole${n}`];
      $(`[data-hd${n}]`).hidden = k !== 'round' && k !== 'd'; $(`[data-df${n}]`).hidden = k !== 'd'; $(`[data-hx${n}]`).hidden = k !== 'hex';
    }
    $('[data-pair-hole]').hidden = !s.pair; $('[data-hole2]').hidden = s.same;
    const big = [1, ...(s.pair ? [2] : [])].some((n) => holeR(holeOf(s, n as 1 | 2)) > (n === 1 ? c1 : c2).rf - 0.8);
    $('[data-hole-big]').hidden = !big; $('[data-hole-big]').textContent = t.holeBig;
    // ступица
    $('[data-hub-opts]').hidden = !s.hub; $('[data-hub-both]').hidden = !s.pair;
    const notes: string[] = [];
    // винту нужен вал: без отверстия его нет (у пары — хотя бы у одной)
    const shaft = holeOf(s, 1).kind !== 'none' || (s.pair && holeOf(s, 2).kind !== 'none');
    $('[data-screw]').hidden = !shaft;
    if (s.screw && shaft) {
      const need = Math.max(...([1, ...(s.pair ? [2] : [])] as (1 | 2)[]).map((n) => holeOf(s, n)).filter((h) => h.kind !== 'none').map((h) => nutMinD(h, s.TOL)));
      notes.push(s.HD < need - 1e-9 ? t.nutNo.replace('{d}', num(Math.ceil(need * 10) / 10)) : t.screwHint.replace('{flat}', holeOf(s, 1).kind === 'd' ? t.flat : ''));
    }
    if (s.pair && s.HD > centerDistance(g1, g2)) notes.push(t.hubHit);
    $('[data-hub-info]').innerHTML = notes.join('<br>'); $('[data-hub-info]').hidden = !notes.length;
    $('[data-view]').hidden = !s.pair; $('[data-spin]').hidden = s.view !== 'mesh';
  }

  // ---------- геометрия ----------
  const dims = stage.querySelector<HTMLElement>('[data-dims]')!;
  const fit = stage.querySelector<HTMLElement>('[data-fit]')!;
  let current: Promise<Model> | null = null;
  const setReady = (on: boolean) => root.querySelectorAll<HTMLButtonElement>('[data-download]').forEach((b) => { b.disabled = !on; });
  // камера ставится один раз; потом — только если модель стала намного больше или меньше, чем при последней постановке
  let seq = 0, timer = 0, framed = 0;
  // для просмотра: где центры в файле и в зацеплении, передаточное
  let place: { file: [number, number][]; mesh: [number, number][]; ratio: number } = { file: [[0, 0]], mesh: [[0, 0]], ratio: 1 };
  const save = () => { try { store?.save({ s }); } catch { /* хранилище недоступно */ } };

  function rebuild() {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      const my = ++seq, p = { ...s };
      save();
      const job = build(p);
      current = job.then((r) => r.model);
      job.then((r) => {
        if (my !== seq) return;
        place = r.place;
        viewer.setParts(r.model.parts);
        const ok = fitsBed(bed, r.w, r.d, r.h);
        fit.className = ok ? 'fit' : 'fit bad';
        fit.textContent = ok ? t.ok : bedWarn(lang, bed);
        setReady(true);
        const ext = Math.max(r.w, r.d, r.h);
        if (!framed || ext > framed * 1.8 || ext < framed / 2.5) { viewer.frame(r.w, r.d, r.h); framed = ext; }
        dims.innerHTML = `<b>${num(r.w)}</b> × <b>${num(r.d)}</b> × <b>${num(r.h)}</b> ${t.mm}`;
      }, (err) => {
        console.error(err);
        if (my === seq) { fit.className = 'fit bad'; fit.textContent = t.err; setReady(false); }
      });
    }, 60);
  }

  /** Одна шестерня в центре координат, зуб №1 смотрит по +X; phase — поворот зубьев, градусы. */
  function gear(M: ManifoldToplevel, k: ReturnType<typeof scope>, p: S, n: 1 | 2, hand: 1 | -1, phase: number): Manifold {
    const g = spec(p, n), c = circles(g);
    const fil = 0.38 * p.M;
    let cs: CrossSection = k(new M.CrossSection([outline(g)], 'NonZero'));
    cs = k(k(k(cs.offset(fil, 'Round', 2, 24)).offset(-fil, 'Round', 2, 24)).rotate(phase));
    const H = p.B, twist = p.kind === 'spur' ? 0 : (hand * (H * Math.tan((p.BETA * Math.PI) / 180)) / c.r) * (180 / Math.PI);
    const div = (tw: number) => Math.min(80, Math.max(1, Math.ceil(Math.abs(tw) / 1.5)));
    let body: Manifold;
    if (p.kind === 'herring') {
      const lo = k(M.Manifold.extrude(cs, H / 2, div(twist / 2), twist / 2));
      const hi = k(k(M.Manifold.extrude(k(cs.rotate(twist / 2)), H / 2, div(twist / 2), -twist / 2)).translate([0, 0, H / 2]));
      body = k(lo.add(hi));
    } else body = k(M.Manifold.extrude(cs, H, p.kind === 'helix' ? div(twist) : 0, twist));
    // фаска: пересечение с телом вращения, у торцев радиус меньше наружного на CH
    if (p.CH > 0) {
      const R0 = c.ra - p.CH, e = Math.min(p.CH + 1, H / 2);
      const cone = k(M.Manifold.revolve([[[0, 0], [R0, 0], [R0 + e, e], [R0 + e, H - e], [R0, H], [0, H]]], Math.max(96, 4 * g.z)));
      body = k(body.intersect(cone));
    }
    const h = holeOf(p, n);
    const top = H + (p.hub ? p.HH : 0);
    if (p.hub) body = k(body.add(k(k(M.Manifold.cylinder(p.HH + 0.01, p.HD / 2, p.HD / 2, 96)).translate([0, 0, H - 0.01]))));
    if (h.kind !== 'none' && holeR(h) < c.rf - 0.8) {
      let sh: CrossSection;
      if (h.kind === 'hex') sh = k(M.CrossSection.circle(h.hx / Math.sqrt(3), 6).rotate(30));
      else {
        sh = k(M.CrossSection.circle(h.d / 2, 64));
        if (h.kind === 'd') sh = k(sh.intersect(k(M.CrossSection.square([h.d, h.d * 2]).translate([-h.d / 2 - (h.d - h.df), -h.d]))));
      }
      body = k(body.subtract(k(k(M.Manifold.extrude(sh, top + 2)).translate([0, 0, -1]))));
    }
    // стопорный винт по +X (в лыску), гайка — в паз сверху
    if (p.hub && p.screw && h.kind !== 'none' && p.HD >= nutMinD(h, p.TOL) - 1e-9) {
      const zs = H + p.HH / 2, tn = M3.t + p.TOL, sn = M3.s + p.TOL, e = sn / Math.cos(Math.PI / 6);
      const xn = (holeR(h) + p.HD / 2) / 2;
      body = k(body.subtract(k(k(k(M.Manifold.cylinder(p.HD / 2 + 1, M3.hole / 2 + p.TOL / 2, M3.hole / 2 + p.TOL / 2, 24)).rotate([0, 90, 0])).translate([0, 0, zs]))));
      body = k(body.subtract(k(k(M.Manifold.cube([tn, sn, top + 1 - (zs - e / 2)])).translate([xn - tn / 2, -sn / 2, zs - e / 2]))));
    }
    return body;
  }

  async function build(p: S) {
    const M = await solid();
    const k = scope();
    try {
      const g1 = spec(p, 1), c1 = circles(g1);
      const R = (c: ReturnType<typeof circles>) => Math.max(c.ra, p.hub ? p.HD / 2 : 0);
      const h = p.B + (p.hub ? p.HH : 0);
      const parts: Model['parts'] = [];
      const first = gear(M, k, p, 1, 1, 0);
      if (!p.pair) {
        parts.push({ name: 'gear', role: 'base', geometry: toGeometry(first) });
        place = { file: [[0, 0]], mesh: [[0, 0]], ratio: 1 };
        return { model: { name: `gear-m${num(p.M)}-z${p.Z1}`, parts }, place, w: 2 * R(c1), d: 2 * R(c1), h };
      }
      const g2 = spec(p, 2), c2 = circles(g2), a = centerDistance(g1, g2);
      // у второй против первой должна быть впадина; косые зубья — встречного наклона
      const second = gear(M, k, p, 2, -1, p.Z2 % 2 === 0 ? 180 / p.Z2 : 0);
      const R1 = R(c1), R2 = R(c2), D = R1 + R2 + GAP;
      const f1 = (R1 - R2 - D) / 2, m1 = (c1.ra - c2.ra - a) / 2;
      parts.push({ name: 'gear-1', role: 'base', geometry: toGeometry(k(first.translate([f1, 0, 0]))) });
      parts.push({ name: 'gear-2', role: 'base', geometry: toGeometry(k(second.translate([f1 + D, 0, 0]))) });
      const pl = { file: [[f1, 0], [f1 + D, 0]] as [number, number][], mesh: [[m1, 0], [m1 + a, 0]] as [number, number][], ratio: p.Z1 / p.Z2 };
      return {
        model: { name: `gears-m${num(p.M)}-z${p.Z1}-z${p.Z2}`, parts, objects: parts.map((x, i) => ({ name: x.name, parts: [i], at: [[0, 0, 0]] as [number, number, number][] })) },
        place: pl, w: 2 * R1 + GAP + 2 * R2, d: 2 * Math.max(R1, R2), h,
      };
    } finally {
      k.free();
    }
  }

  // ---------- пара в зацеплении: поворот вокруг своих осей ----------
  let th = 0, last = performance.now();
  (function tick(now: number) {
    const dt = Math.min(0.1, (now - last) / 1000); last = now;
    const meshed = s.pair && s.view === 'mesh' && place.mesh.length === 2;
    if (meshed && s.spin) th += dt * 0.4;
    viewer.meshes().forEach((m, i) => {
      if (!meshed || !place.file[i]) { m.rotation.z = 0; m.position.set(0, 0, 0); return; }
      const a = i === 0 ? th : -th * place.ratio, [fx, fy] = place.file[i], [vx, vy] = place.mesh[i];
      const c = Math.cos(a), sn = Math.sin(a);
      m.rotation.z = a;
      m.position.set(vx - (c * fx - sn * fy), vy - (sn * fx + c * fy), 0);
    });
    requestAnimationFrame(tick);
  })(last);

  sync();
  rebuild();
  return { model: () => current ?? build(s).then((r) => r.model) };
}
